import { isNull } from "../utils/is-null-or-undefined.js";
import { isObject } from "../utils/is-object.js";
import { HOUR_TO_MS, MINUTE_TO_MS, SECOND_TO_MS } from "./time-constants.js";
export const PosixDateTransformer = {
    from(value) {
        if (isNull(value)) {
            return undefined;
        }
        // TypeORM/mysql2 devuelven las columnas `bigint` como STRING numérico
        // (p. ej. "1783528858576") para no perder precisión. Las columnas
        // `date`/`datetime` vuelven como 'YYYY-MM-DD[ HH:mm:ss]' o como objeto
        // Date. Un string de solo dígitos es epoch-ms (Number); cualquier otra
        // cosa se parsea como fecha. Sin este branch, `new Date("1783...")` da
        // Invalid Date y toda columna bigint (createdAt/updatedAt/deletedAt,
        // etc.) se leía como `undefined`.
        let timestamp;
        if (typeof value === "number") {
            timestamp = value;
        }
        else if (typeof value === "string" && /^-?\d+$/.test(value.trim())) {
            timestamp = Number(value);
        }
        else {
            timestamp = new Date(value).getTime();
        }
        return !isNaN(timestamp) ? new PosixDate(timestamp) : undefined;
    },
    to(value) {
        return value ? value.timestamp : null;
    },
};
/**
 * Transformer para columnas de tipo `date` (fechas puras: nacimiento, inicio/fin
 * de actividad, vencimiento, etc.). A diferencia de `PosixDateTransformer` (que
 * guarda el timestamp en ms, para columnas bigint), éste guarda la fecha como
 * `YYYY-MM-DD` — legible y compatible con columnas `date` de MySQL.
 * La lectura reutiliza el `from` (que ya interpreta tanto strings como números).
 */
export const PosixDateOnlyTransformer = {
    from: PosixDateTransformer.from,
    to(value) {
        return value
            ? new Date(value.timestamp).toISOString().slice(0, 10)
            : null;
    },
};
export class PosixDate {
    timestamp;
    constructor(timestamp) {
        this.timestamp = timestamp ?? Date.now();
    }
    toJSON() {
        return {
            type: "posix-date",
            timestamp: this.timestamp,
        };
    }
    static fromJson(json) {
        return new PosixDate(json.timestamp);
    }
    static isPosixDateJSON(value) {
        if (isObject(value) &&
            "type" in value &&
            "timestamp" in value &&
            value["type"] === "posix-date" &&
            typeof value["timestamp"] === "number")
            return true;
        return false;
    }
    toLocaleString(opts, locale = "en-US") {
        const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return new Date(this.timestamp).toLocaleString(locale, {
            timeZone: currentTimezone,
            ...opts,
        });
    }
    isAfter(date) {
        return this.timestamp > date.timestamp;
    }
    isBefore(date) {
        return this.timestamp < date.timestamp;
    }
    isEqualTo(date) {
        return this.timestamp === date.timestamp;
    }
    addHours(hours) {
        return new PosixDate(this.timestamp + hours * HOUR_TO_MS);
    }
    addDays(days) {
        return new PosixDate(this.timestamp + days * HOUR_TO_MS * 24);
    }
    addMinutes(minutes) {
        return new PosixDate(this.timestamp + minutes * MINUTE_TO_MS);
    }
    addSeconds(seconds) {
        return new PosixDate(this.timestamp + seconds * SECOND_TO_MS);
    }
    subtractDays(days) {
        return new PosixDate(this.timestamp - days * HOUR_TO_MS * 24);
    }
    subtractHours(hours) {
        return new PosixDate(this.timestamp - hours * HOUR_TO_MS);
    }
    subtractMinutes(minutes) {
        return new PosixDate(this.timestamp - minutes * MINUTE_TO_MS);
    }
    subtractSeconds(seconds) {
        return new PosixDate(this.timestamp - seconds * SECOND_TO_MS);
    }
    getDateString() {
        return new Date(this.timestamp).toISOString().split("T")[0];
    }
    getTimeString() {
        return new Date(this.timestamp)
            .toISOString()
            .split("T")[1]
            .split(".")[0];
    }
}
